import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';
import { toast } from 'react-toastify';
import notificationService from '../services/notification.service';

const SNOOZE_OPTIONS = [
  { value: 'off', label: 'Emails on (not snoozed)' },
  { value: '1', label: '1 day' },
  { value: '3', label: '3 days' },
  { value: '7', label: '1 week' },
  { value: 'indefinite', label: 'Until I turn off' },
] as const;

type SnoozeOptionValue = (typeof SNOOZE_OPTIONS)[number]['value'];

const INDEFINITE_UNTIL = '2099-12-31T23:59:59.000Z';

function resolveOptionFromUntil(isSnoozed: boolean, snoozedUntil: string | null): SnoozeOptionValue {
  if (!isSnoozed || !snoozedUntil) {
    return 'off';
  }

  const until = new Date(snoozedUntil).getTime();
  if (until >= new Date(INDEFINITE_UNTIL).getTime() - 24 * 60 * 60 * 1000) {
    return 'indefinite';
  }

  const daysRemaining = Math.max(1, Math.ceil((until - Date.now()) / (24 * 60 * 60 * 1000)));
  if (daysRemaining <= 1) return '1';
  if (daysRemaining <= 3) return '3';
  if (daysRemaining <= 7) return '7';
  return 'indefinite';
}

function snoozeUntilForOption(option: SnoozeOptionValue): string | null {
  if (option === 'off') {
    return null;
  }
  if (option === 'indefinite') {
    return INDEFINITE_UNTIL;
  }

  const until = new Date();
  until.setDate(until.getDate() + Number(option));
  return until.toISOString();
}

const NotificationSnoozeSettings: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isSnoozed, setIsSnoozed] = useState(false);
  const [snoozedUntil, setSnoozedUntil] = useState<string | null>(null);
  const [selectedOption, setSelectedOption] = useState<SnoozeOptionValue>('off');

  const loadPreferences = async () => {
    try {
      setLoading(true);
      const response = await notificationService.getPreferences();
      if (response.success) {
        setIsSnoozed(response.data.is_snoozed);
        setSnoozedUntil(response.data.email_snoozed_until);
        setSelectedOption(
          resolveOptionFromUntil(response.data.is_snoozed, response.data.email_snoozed_until)
        );
      }
    } catch {
      toast.error('Failed to load notification preferences');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPreferences();
  }, []);

  const applySnooze = async (option: SnoozeOptionValue) => {
    setSaving(true);
    try {
      const response = await notificationService.updateSnooze(snoozeUntilForOption(option));
      if (response.success) {
        setIsSnoozed(response.data.is_snoozed);
        setSnoozedUntil(response.data.email_snoozed_until);
        setSelectedOption(option);
        toast.success(
          option === 'off'
            ? 'Email notifications resumed'
            : option === 'indefinite'
              ? 'Email notifications snoozed until you turn them back on'
              : `Email notifications snoozed for ${SNOOZE_OPTIONS.find(o => o.value === option)?.label}`
        );
      }
    } catch {
      toast.error('Failed to update snooze settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <Typography variant="body2">Loading notification settings…</Typography>;
  }

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        While snoozed, emails are not sent. Notifications appear in the app instead.
      </Typography>

      <FormControl component="fieldset" disabled={saving} sx={{ width: '100%', mb: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Choose snooze option
        </Typography>
        <RadioGroup
          value={selectedOption}
          onChange={(e) => setSelectedOption(e.target.value as SnoozeOptionValue)}
        >
          {SNOOZE_OPTIONS.map((option) => (
            <FormControlLabel
              key={option.value}
              value={option.value}
              control={<Radio />}
              label={option.label}
            />
          ))}
        </RadioGroup>
      </FormControl>

      {isSnoozed && snoozedUntil ? (
        <Typography variant="body2" sx={{ mb: 2 }}>
          {new Date(snoozedUntil).getFullYear() >= 2099
            ? 'Currently snoozed until you turn it off'
            : `Currently snoozed until ${new Date(snoozedUntil).toLocaleString()}`}
        </Typography>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Email notifications are currently on.
        </Typography>
      )}

      <Button
        variant="contained"
        onClick={() => applySnooze(selectedOption)}
        disabled={saving}
      >
        {saving ? 'Saving…' : 'Apply snooze setting'}
      </Button>
    </Box>
  );
};

export default NotificationSnoozeSettings;
